import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faBell } from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Dropdown,
} from "@themesberg/react-bootstrap";

import {
  adminAllCourseList,
  adminGetUserByCourse,
  adminSendNotifications,
} from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
  background: "linear-gradient(90deg, #2c60ba 0%, #005dfb 100%)",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

// const audienceTypeOptions = [
//   { value: "", label: "Select Audience Type" },
//   { value: "all", label: "All Users" },
//   { value: "specific", label: "Specific Users" },
//   // { value: "segment", label: "Segment" },
// ];

const audienceTypeOptions = [
  { value: "", label: "Select Audience Type" },
  { value: "all", label: "All Users" },
  { value: "active", label: "Active Users" },
  { value: "inactive", label: "Inactive Users" },
];

// const segmentOptions = [
//   { value: "students", label: "Students" },
//   { value: "teachers", label: "Teachers" },
//   { value: "parents", label: "Parents" },
//   { value: "staff", label: "Staff" },
// ];

const initialFormData = {
  title: "",
  audienceType: "",
  // course: "",
  course: [],
  audience: [],
  messageBody: "",
  // sendVia: [],
  scheduleSend: "",
  attachment: null,
};

const initialErrors = {
  title: "",
  audienceType: "",
  course: "",
  audience: "",
  messageBody: "",
  // sendVia: "",
};

const mapCourseItem = (item) => ({
  value: String(item?.id || ""),
  label: item?.title || "Untitled Course",
});

const mapUserItem = (item) => ({
  value: String(item?.id || ""),
  label: item?.name || "Unnamed User",
  email: item?.email || "",
});

export default function AdminSendNotifications() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  // const [courseOptions, setCourseOptions] = useState([
  //   { value: "", label: "Select Course" },
  // ]);

  const [courseOptions, setCourseOptions] = useState([
    { value: "all", label: "All Courses" },
  ]);

  const [loadingCourses, setLoadingCourses] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [audienceDropdownOpen, setAudienceDropdownOpen] = useState(false);

  const scheduleSendRef = useRef(null);
  const attachmentRef = useRef(null);

  const audienceOptions = useMemo(() => {
    if (formData.audienceType === "specific") return userOptions;
    // if (formData.audienceType === "segment") return segmentOptions;
    return [];
  }, [formData.audienceType, userOptions]);

  const selectedAudienceSummary = useMemo(() => {
    if (formData.audienceType !== "specific") return "";
    if (formData.audience.length === 0) return "Select Audience / Recipients";
    if (formData.audience.length === 1) {
      const selectedUser = userOptions.find(
        (u) => u.value === formData.audience[0]
      );
      return selectedUser?.label || "1 selected";
    }
    return `${formData.audience.length} selected`;
  }, [formData.audienceType, formData.audience, userOptions]);

  const selectedCourseSummary = useMemo(() => {
    if (formData.course.length === 0) {
      return "Select Course";
    }

    if (formData.course.includes("all")) {
      return "All Courses";
    }

    if (formData.course.length === 1) {
      const selected = courseOptions.find(
        (c) => c.value === formData.course[0]
      );

      return selected?.label || "1 selected";
    }

    return `${formData.course.length} selected`;
  }, [formData.course, courseOptions]);

  const submitButtonLabel = formData.scheduleSend ? "Schedule" : "Send";
  const submitButtonIcon = formData.scheduleSend ? faBell : faPaperPlane;

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoadingCourses(true);

        const response = await adminAllCourseList();
        const responseData = response?.data || {};
        const data = responseData?.data || {};
        const courses = Array.isArray(data?.courses) ? data.courses : [];

        // const mappedCourses = courses.map(mapCourseItem);
        // setCourseOptions([{ value: "", label: "Select Course" }, ...mappedCourses]);

        const mappedCourses = courses.map(mapCourseItem);

        setCourseOptions([
          { value: "all", label: "All Courses" },
          ...mappedCourses,
        ]);

      } catch (error) {
        console.error("Failed to fetch courses:", error);
        toast.error(error?.response?.data?.message || "Unable to load course list.");
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      // const courseId = formData.course;

      // if (!courseId || formData.audienceType !== "specific") {
      const courseIds = formData.course;

      if (
        courseIds.length === 0 ||
        formData.audienceType !== "specific"
      ) {
        setUserOptions([]);
        setLoadingUsers(false);
        return;
      }

      try {
        setLoadingUsers(true);

        // const response = await adminGetUserByCourse(courseId);
        const response = await adminGetUserByCourse(courseIds);
        const responseData = response?.data || {};
        const users = Array.isArray(responseData?.data) ? responseData.data : [];

        setUserOptions(users.map(mapUserItem));
      } catch (error) {
        console.error("Failed to fetch users by course:", error);
        toast.error(error?.response?.data?.message || "Unable to load users.");
        setUserOptions([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [formData.course, formData.audienceType]);

  const toggleAudienceUser = (userId) => {
    setFormData((prev) => {
      const exists = prev.audience.includes(userId);

      return {
        ...prev,
        audience: exists
          ? prev.audience.filter((id) => id !== userId)
          : [...prev.audience, userId],
      };
    });

    setErrors((prev) => ({
      ...prev,
      audience: "",
    }));
  };


  const toggleCourse = (courseId) => {
    setFormData((prev) => {
      let selected = [...prev.course];

      if (courseId === "all") {
        if (selected.includes("all")) {
          // Unselect everything
          selected = [];
        } else {
          // Select all courses including "all"
          selected = courseOptions.map((item) => item.value);
        }
      } else {
        // Remove "all" first
        selected = selected.filter((id) => id !== "all");

        if (selected.includes(courseId)) {
          selected = selected.filter((id) => id !== courseId);
        } else {
          selected.push(courseId);
        }

        // If all individual courses are selected, automatically select "all"
        const allCourseIds = courseOptions
          .filter((item) => item.value !== "all")
          .map((item) => item.value);

        const isAllSelected = allCourseIds.every((id) =>
          selected.includes(id)
        );

        if (isAllSelected) {
          selected = ["all", ...allCourseIds];
        }
      }

      return {
        ...prev,
        course: selected,
        audience: [],
      };
    });

    setErrors((prev) => ({
      ...prev,
      course: "",
    }));
  };

  const clearAttachmentInput = () => {
    if (attachmentRef.current) {
      attachmentRef.current.value = "";
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors(initialErrors);
    setUserOptions([]);
    setAudienceDropdownOpen(false);
    clearAttachmentInput();
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    // if (type === "checkbox") {
    //     setFormData((prev) => {
    //         const existing = prev.sendVia || [];
    //         const updated = checked
    //             ? [...existing, value]
    //             : existing.filter((item) => item !== value);
    //
    //         return {
    //             ...prev,
    //             sendVia: updated,
    //         };
    //     });
    //
    //     setErrors((prev) => ({
    //         ...prev,
    //         sendVia: "",
    //     }));
    //
    //     return;
    // }

    if (type === "file") {
      const file = files && files[0] ? files[0] : null;

      if (file) {
        const MAX_FILE_SIZE = 10 * 1024 * 1024;

        if (file.size > MAX_FILE_SIZE) {
          toast.error("File size exceeds 10MB limit.", {
            autoClose: 3000,
          });
          e.target.value = "";
          return;
        }
      }

      setFormData((prev) => ({
        ...prev,
        attachment: file,
      }));

      return;
    }

    if (name === "audienceType") {
      setFormData((prev) => ({
        ...prev,
        audienceType: value,
        audience: [],
      }));

      setErrors((prev) => ({
        ...prev,
        audienceType: "",
        audience: "",
        course: "",
      }));

      setAudienceDropdownOpen(false);
      return;
    }

    if (name === "course") {
      setFormData((prev) => ({
        ...prev,
        course: value,
        audience: [],
      }));

      setErrors((prev) => ({
        ...prev,
        course: "",
        audience: "",
      }));

      setAudienceDropdownOpen(false);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "title" || name === "messageBody" || name === "scheduleSend") {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const needsCourse =
      formData.audienceType === "all" || formData.audienceType === "specific";
    const needsAudience = formData.audienceType === "specific";

    const newErrors = {
      title: formData.title.trim() ? "" : "Notification Title is required",
      audienceType: formData.audienceType ? "" : "Audience Type is required",
      // course: needsCourse && !formData.course ? "Course is required" : "",
      course:
        needsCourse && formData.course.length === 0
          ? "Please select at least one course"
          : "",
      audience:
        needsAudience && formData.audience.length === 0
          ? "Please select at least one audience"
          : "",
      messageBody: formData.messageBody.trim() ? "" : "Message Body is required",
      // sendVia:
      //     formData.sendVia.length > 0
      //         ? ""
      //         : "Please select at least one send channel",
    };

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((msg) => msg !== "");
    if (hasError) {
      toast.error("Please correct the highlighted fields.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = new FormData();
      payload.append("title", formData.title.trim());
      payload.append("audience_type", formData.audienceType);
      payload.append("message", formData.messageBody.trim());

      // if (formData.course) {
      //   payload.append("course_id", formData.course);
      // }

      // Course Payload
      if (formData.course.includes("all")) {
        payload.append("course_type", "all");
      }
      else {
        const selectedCourses = formData.course.filter((id) => id !== "all");

        if (selectedCourses.length === 1) {
          payload.append("course_type", "specific");
          payload.append("course_id", selectedCourses[0]);
        } else if (selectedCourses.length > 1) {
          payload.append("course_type", "multiple");
          selectedCourses.forEach((id) => {
            payload.append("course_ids[]", id);
          });
        }
      }

      // if (formData.audienceType === "specific") {
      //   formData.audience.forEach((item) => {
      //     payload.append("user_ids[]", item);
      //   });
      // }

      // if (formData.audienceType === "specific" || formData.audienceType === "segment") {
      //   formData.audience.forEach((item) => {
      //     payload.append("user_ids[]", item);
      //   });
      // }

      if (formData.attachment) {
        payload.append("attachment", formData.attachment);
      }

      if (formData.scheduleSend) {
        payload.append("scheduled_at", formData.scheduleSend);
      }

      const response = await adminSendNotifications(payload);
      const responseData = response?.data || {};

      if (responseData?.success === false) {
        toast.error(responseData?.message || "Unable to send notification.");
        return;
      }

      if (formData.scheduleSend) {
        toast.success(
          responseData?.message || "Notification scheduled successfully."
        );
        setSuccessMessage(
          responseData?.message || "Notification scheduled successfully."
        );
      } else {
        toast.success(responseData?.message || "Notification sent successfully.");
        setSuccessMessage(
          responseData?.message || "Notification sent successfully."
        );
      }

      resetForm();
      clearAttachmentInput();

      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error("Failed to send notification:", error);
      toast.error(error?.response?.data?.message || "Unable to send notification.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="notification-header py-4">
        <div className="notification-header-title">
          {/* <h5 className="mb-0">Send Notifications</h5> */}
          <h2 className="h4 mb-1">Send Notifications</h2>
        </div>

        {/* <div className="notification-header-actions">
          <Button
            type="submit"
            form="sendNotificationForm"
            className="notification-send-btn fw-semibold"
            style={ACTIVE_BUTTON_STYLE}
          >
            <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
            Send Notification
          </Button>
        </div> */}
      </div>

      {/* {successMessage ? (
        <div className="alert alert-success border-0 shadow-sm mb-3">
          {successMessage}
        </div>
      ) : null} */}

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <Form id="sendNotificationForm" onSubmit={handleSubmit}>
            <Row>
              <Col lg={12} className="mb-3">
                <Form.Group controlId="notificationTitle">
                  <Form.Label className="fw-semibold">
                    Notification Title <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="title"
                    type="text"
                    placeholder="Enter notification title"
                    value={formData.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                    style={{ boxShadow: "none" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.title}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col lg={6} className="mb-3">
                <Form.Group controlId="course">
                  <Form.Label className="fw-semibold">
                    Course <span className="text-danger">*</span>
                  </Form.Label>
                  {/* <Form.Select
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    isInvalid={!!errors.course}
                    style={{ boxShadow: "none" }}
                    disabled={loadingCourses}
                  >
                    {courseOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {loadingCourses && item.value === ""
                          ? "Loading courses..."
                          : item.label}
                      </option>
                    ))}
                  </Form.Select> 
                  
                  <Form.Control.Feedback type="invalid">
                    {errors.course}
                  </Form.Control.Feedback>*/}

                  <Dropdown
                    autoClose="outside"
                    show={courseDropdownOpen}
                    onToggle={(nextShow, _, metadata) => {
                      if (loadingCourses) return;

                      if (metadata?.source === "select" && nextShow === false) return;

                      setCourseDropdownOpen(nextShow);
                    }}
                    className="w-100"
                  >
                    <Dropdown.Toggle
                      as="button"
                      type="button"
                      className={`w-100 text-start border form-select ${errors.course ? "is-invalid" : ""
                        }`}
                      disabled={loadingCourses}
                      style={{
                        boxShadow: "none",
                        backgroundColor: "#fff",
                        paddingRight: "2.5rem",
                      }}
                    >
                      <span className="d-block text-truncate">
                        {loadingCourses ? "Loading courses..." : selectedCourseSummary}
                      </span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                      className="w-100 p-2 shadow-sm"
                      style={{
                        maxHeight: "250px",
                        overflowY: "auto",
                        zIndex: 1050,
                      }}
                    >
                      {courseOptions.map((item) => (
                        <Dropdown.Item
                          as="button"
                          type="button"
                          key={item.value}
                          className="px-2 py-1"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleCourse(item.value);
                          }}
                        >
                          <Form.Check
                            type="checkbox"
                            id={`course-${item.value}`}
                            checked={formData.course.includes(item.value)}
                            readOnly
                            onClick={(e) => e.preventDefault()}
                            label={item.label}
                          />
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  {errors.course && (
                    <div className="invalid-feedback d-block">
                      {errors.course}
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col lg={6} className="mb-3">
                <Form.Group controlId="audienceType">
                  <Form.Label className="fw-semibold">
                    Audience Type <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="audienceType"
                    value={formData.audienceType}
                    onChange={handleChange}
                    isInvalid={!!errors.audienceType}
                    style={{ boxShadow: "none" }}
                  >
                    {audienceTypeOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.audienceType}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {formData.audienceType === "specific" && (
                <Col lg={12} className="mb-3">
                  <Form.Group controlId="audience">
                    <Form.Label className="fw-semibold">
                      Select Audience / Recipients{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>

                    <Dropdown
                      autoClose="outside"
                      show={audienceDropdownOpen}
                      onToggle={(nextShow, _, metadata) => {
                        if (!formData.course || loadingUsers) return;
                        if (metadata?.source === "select" && nextShow === false) return;
                        setAudienceDropdownOpen(nextShow);
                      }}
                      className="w-100"
                    >
                      <Dropdown.Toggle
                        as="button"
                        type="button"
                        className={`w-100 text-start border form-select ${errors.audience ? "is-invalid" : ""
                          }`}
                        // disabled={!formData.course || loadingUsers}
                        disabled={formData.course.length === 0 || loadingUsers}
                        style={{
                          boxShadow: "none",
                          backgroundColor: "#fff",
                          paddingRight: "2.5rem",
                        }}
                      >
                        <span className="d-block text-truncate">
                          {loadingUsers
                            ? "Loading users..."
                            : selectedAudienceSummary}
                        </span>
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        className="w-100 p-2 shadow-sm"
                        style={{
                          maxHeight: "240px",
                          overflowY: "auto",
                          zIndex: 1050,
                        }}
                      >
                        {userOptions.length > 0 ? (
                          userOptions.map((item) => (
                            <Dropdown.Item
                              as="button"
                              type="button"
                              key={item.value}
                              className="px-2 py-1"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleAudienceUser(item.value);
                              }}
                            >
                              <Form.Check
                                type="checkbox"
                                id={`audience-user-${item.value}`}
                                checked={formData.audience.includes(item.value)}
                                readOnly
                                onClick={(e) => e.preventDefault()}
                                label={
                                  <div className="d-flex flex-column">
                                    <span className="fw-semibold">{item.label}</span>
                                    {item.email ? (
                                      <small className="text-muted">{item.email}</small>
                                    ) : null}
                                  </div>
                                }
                              />
                            </Dropdown.Item>
                          ))
                        ) : (
                          <div className="px-2 py-2 text-muted">
                            {/* {formData.course ? "No users found." : "Select course first."} */}
                            {formData.course.length > 0
                              ? "No users found."
                              : "Select course first."}
                          </div>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>

                    {errors.audience ? (
                      <div className="invalid-feedback d-block">{errors.audience}</div>
                    ) : null}
                  </Form.Group>
                </Col>
              )}

              {/* {formData.audienceType === "segment" && (
                <Col lg={12} className="mb-3">
                  <Form.Group controlId="audience">
                    <Form.Label className="fw-semibold">
                      Select Segment <span className="text-danger">*</span>
                    </Form.Label>

                    <Dropdown autoClose="outside" className="w-100">
                      <Dropdown.Toggle
                        variant="light"
                        className={`w-100 text-start border ${
                          errors.audience ? "is-invalid" : ""
                        }`}
                        style={{ boxShadow: "none" }}
                      >
                        {formData.audience.length > 0
                          ? `${formData.audience.length} selected`
                          : "Select Segment"}
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        className="w-100 p-2"
                        style={{ maxHeight: "240px", overflowY: "auto" }}
                      >
                        {segmentOptions.map((item) => (
                          <Dropdown.Item as="div" key={item.value} className="px-2 py-1">
                            <Form.Check
                              type="checkbox"
                              id={`segment-${item.value}`}
                              checked={formData.audience.includes(item.value)}
                              onChange={() => toggleAudienceUser(item.value)}
                              label={item.label}
                            />
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>

                    {errors.audience ? (
                      <div className="invalid-feedback d-block">
                        {errors.audience}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>
              )} */}

              <Col lg={12} className="mb-3">
                <Form.Group controlId="messageBody">
                  <Form.Label className="fw-semibold">
                    Message Body <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="messageBody"
                    rows={5}
                    placeholder="Write notification message here"
                    value={formData.messageBody}
                    onChange={handleChange}
                    isInvalid={!!errors.messageBody}
                    style={{ boxShadow: "none" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.messageBody}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* <Col lg={6} className="mb-3">
                <Form.Group controlId="sendVia">
                    <Form.Label className="fw-semibold d-block">
                        Send Via <span className="text-danger">*</span>
                    </Form.Label>

                    <div className="d-flex flex-wrap gap-3">
                        <Form.Check
                            type="checkbox"
                            label="App"
                            value="app"
                            checked={formData.sendVia.includes("app")}
                            onChange={handleChange}
                        />
                        <Form.Check
                            type="checkbox"
                            label="SMS"
                            value="sms"
                            checked={formData.sendVia.includes("sms")}
                            onChange={handleChange}
                        />
                        <Form.Check
                            type="checkbox"
                            label="Email"
                            value="email"
                            checked={formData.sendVia.includes("email")}
                            onChange={handleChange}
                        />
                    </div>

                    {errors.sendVia ? (
                        <div className="invalid-feedback d-block">
                            {errors.sendVia}
                        </div>
                    ) : null}
                </Form.Group>
              </Col> */}

              <Col lg={6} className="mb-3">
                <Form.Group controlId="scheduleSend">
                  <Form.Label className="fw-semibold">Schedule Send</Form.Label>
                  <Form.Control
                    ref={scheduleSendRef}
                    name="scheduleSend"
                    type="datetime-local"
                    value={formData.scheduleSend}
                    onChange={handleChange}
                    onMouseDown={() => {
                      const input = scheduleSendRef.current;
                      if (input && typeof input.showPicker === "function") {
                        input.showPicker();
                      }
                    }}
                    className="w-100 schedule-datetime-input"
                    style={{ boxShadow: "none", cursor: "pointer" }}
                  />
                  <Form.Text className="text-muted">
                    Leave empty to send immediately.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col lg={6} className="mb-3">
                <Form.Group controlId="attachment">
                  <Form.Label className="fw-semibold">Attachment</Form.Label>
                  <Form.Control
                    ref={attachmentRef}
                    name="attachment"
                    type="file"
                    onChange={handleChange}
                    style={{ boxShadow: "none" }}
                  />
                  {/* <Form.Text className="text-muted">
                      Optional file attachment.
                  </Form.Text> */}
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-3">
              {/* <Button
                type="button"
                variant="outline-secondary"
                onClick={resetForm}
              >
                Reset
              </Button> */}
              <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={submitting}>
                <FontAwesomeIcon icon={submitButtonIcon} className="me-2" />
                {submitting ? "Processing..." : submitButtonLabel}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <style>{`
        .notification-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 24px 0;
        }

        .notification-header-title {
          flex: 0 0 auto;
        }

        .notification-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex: 1;
        }

        .notification-send-btn {
          white-space: nowrap;
          flex: 0 0 auto;
          padding: 10px 20px;
        }

        .schedule-datetime-input {
          width: 100%;
        }

        .schedule-datetime-input::-webkit-calendar-picker-indicator {
          cursor: pointer;
        }

        .dropdown-toggle.form-select::after {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
        }

        .dropdown-toggle.form-select {
          position: relative;
          min-height: calc(1.5em + 0.75rem + 2px);
          border-color: #ced4da;
        }

        .dropdown-toggle.form-select.is-invalid {
          border-color: #dc3545;
        }

        @media (max-width: 767.98px) {
          .notification-header {
            flex-direction: column;
            align-items: stretch;
          }

          .notification-header-title {
            width: 100%;
          }

          .notification-header-actions {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
          }

          .notification-send-btn {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
}